import { doc, getDoc } from "firebase/firestore";
import { SERVER_URL } from "../config.js";
import {
  normalizeMyTasselPlusSlug,
  isReservedMyTasselPlusSlug,
  getMyTasselPlusSlugCandidates,
} from "../constants/MyTasselPlus";

export {
  normalizeMyTasselPlusSlug,
  isReservedMyTasselPlusSlug,
  getMyTasselPlusSlugCandidates,
};

export const isLegacyMyTasselUid = (value = "") => /^[A-Za-z]\d{5}$/.test(String(value));

export const extractMyTasselRouteSegment = (pathname, prefix) => {
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = new RegExp(`/${escaped}/([^/?#]+)`).exec(pathname || "");
  return match?.[1] || null;
};

export const extractMyTasselVanitySlug = (pathname, pageType = "events") => {
  const normalizedType = pageType === "registry" ? "registry" : "events";
  const cleanPath = String(pathname || "")
    .split(/[?#]/)[0]
    .replace(/\/+$/, "") || "/";

  if (normalizedType === "registry") {
    const registryMatch = /^\/([^/]+)\/registry$/.exec(cleanPath);
    return registryMatch?.[1] || null;
  }

  if (normalizedType === "events") {
    const profileMatch = /^\/([^/]+)$/.exec(cleanPath);
    if (profileMatch?.[1]) return profileMatch[1];
  }

  return null;
};

export const resolveMyTasselRouteUid = async ({ db, segment, slug, pageType = "events" }) => {
  if (segment && isLegacyMyTasselUid(segment)) return segment;

  const normalizedSlug = normalizeMyTasselPlusSlug(slug || segment || "");
  if (!normalizedSlug || isReservedMyTasselPlusSlug(normalizedSlug)) return null;

  const slugSnap = await getDoc(doc(db, "slugs", normalizedSlug));
  if (!slugSnap.exists()) return null;

  const data = slugSnap.data();
  if (data.active === false) {
    const aliasExpiresAt = data.aliasExpiresAt || data.expiresAt || data.reservationExpiresAt;
    const aliasExpirationDate = aliasExpiresAt?.toDate ? aliasExpiresAt.toDate() : aliasExpiresAt ? new Date(aliasExpiresAt) : null;
    if (data.alias !== true || !aliasExpirationDate || aliasExpirationDate <= new Date()) return null;
  }
  if (data.pageType && data.pageType !== "all" && data.pageType !== pageType) return null;

  return data.uid || null;
};

export const getMyTasselPublicUrls = ({ pageOwner = {}, pageOwnerId = "", baseUrl = "https://mytassel.com" } = {}) => {
  const slug = normalizeMyTasselPlusSlug(pageOwner?.myTasselPlus?.slug || pageOwner?.slug || "");
  const uid = pageOwnerId || pageOwner?.uid || "";
  const base = String(baseUrl || "https://mytassel.com").replace(/\/$/, "");

  if (slug) {
    return {
      announcement: `${base}/${slug}`,
      events: `${base}/${slug}`,
      registry: `${base}/${slug}/registry`,
      usesSlug: true,
    };
  }

  return {
    announcement: `${base}/events/${uid}`,
    events: `${base}/events/${uid}`,
    registry: `${base}/registry/${uid}`,
    usesSlug: false,
  };
};

export const claimMyTasselPlusSlug = async (authUser, slug) => {
  if (!authUser?.getIdToken) {
    throw new Error("Please sign in before saving a personalized URL.");
  }

  const normalizedSlug = normalizeMyTasselPlusSlug(slug);
  if (!normalizedSlug) {
    throw new Error("Enter a personalized URL.");
  }
  if (isReservedMyTasselPlusSlug(normalizedSlug)) {
    throw new Error("That personalized URL is reserved.");
  }

  const token = await authUser.getIdToken();
  const response = await fetch(`${SERVER_URL}/mytassel-plus/claim-slug`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ slug: normalizedSlug }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || "Could not save personalized URL.");
    error.data = data;
    error.status = response.status;
    throw error;
  }

  return data;
};

export const checkMyTasselPlusSlugAvailability = async (authUser, slug) => {
  if (!authUser?.getIdToken) {
    throw new Error("Please sign in before checking a personalized URL.");
  }

  const normalizedSlug = normalizeMyTasselPlusSlug(slug);
  if (!normalizedSlug) {
    throw new Error("Enter a personalized URL.");
  }
  if (isReservedMyTasselPlusSlug(normalizedSlug)) {
    throw new Error("That personalized URL is reserved.");
  }

  const token = await authUser.getIdToken();
  const response = await fetch(`${SERVER_URL}/mytassel-plus/check-slug?slug=${encodeURIComponent(normalizedSlug)}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || "Could not check personalized URL.");
    error.data = data;
    error.status = response.status;
    throw error;
  }

  return data;
};
